import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getERPClient } from "@/lib/erp/client";
import type { PackedItem } from "@/lib/erp/types/order";
import type { BundleVariantSelections } from "@/features/selling/utils/orderBundleData";

interface OrderItemBundleSectionProps {
  packedItems: PackedItem[];
  variantSelections?: BundleVariantSelections;
}

/** Fetch variant attribute labels by reading each Item doc's `attributes`
 *  child table. Item Variant Attribute is a child DocType with no standalone
 *  permissions, so we fetch the parent Item instead (which has proper perms). */
function useVariantAttributes(variantCodes: string[]) {
  return useQuery({
    queryKey: ["variant-attributes", ...variantCodes.sort()],
    queryFn: async () => {
      if (variantCodes.length === 0) return {};
      const client = getERPClient();
      const result: Record<string, string[]> = {};
      await Promise.all(
        variantCodes.map(async (code) => {
          try {
            const item = await client.get<{
              attributes?: { attribute: string; attribute_value: string }[];
            }>({ doctype: "Item", name: code });
            const labels = (item.attributes ?? [])
              .filter((a) => a.attribute_value)
              .map((a) => `${a.attribute}: ${a.attribute_value}`);
            if (labels.length > 0) result[code] = labels;
          } catch {
            // Item not found or no permission — skip
          }
        })
      );
      return result;
    },
    enabled: variantCodes.length > 0,
    staleTime: 60_000,
  });
}

export function OrderItemBundleSection({
  packedItems,
  variantSelections = {},
}: OrderItemBundleSectionProps) {
  const [expanded, setExpanded] = useState(true);

  // Collect selected variant codes for attribute lookup
  const variantCodes = Object.values(variantSelections).filter(Boolean);
  const { data: variantAttrs } = useVariantAttributes(variantCodes);

  if (packedItems.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Bundle contents"
      className="border border-dashed border-border/60 bg-muted/30 rounded-xl px-4 py-3 mt-3 animate-fade-in motion-reduce:animate-none"
    >
      {/* Header — clickable to collapse */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 w-full text-left mb-2 last:mb-0"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
            !expanded && "-rotate-90"
          )}
        />
        <span className="text-xs font-medium text-muted-foreground">
          รายการในชุด ({packedItems.length} รายการ)
        </span>
      </button>

      {/* Sub-items list */}
      {expanded && (
        <ul className="space-y-2">
          {packedItems.map((item) => {
            const selectedVariant = variantSelections[item.item_code];
            const attrs = selectedVariant ? variantAttrs?.[selectedVariant] : undefined;

            return (
              <li key={item.name} className="flex items-start gap-2">
                <span className="text-muted-foreground mt-0.5 leading-none">
                  •
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-foreground font-medium truncate">
                      {item.item_name || item.item_code}
                    </span>
                    <span className="text-sm text-muted-foreground flex-shrink-0">
                      ×{item.qty}
                    </span>
                  </div>
                  {attrs && attrs.length > 0 ? (
                    <span className="text-xs text-muted-foreground">
                      {attrs.join(" · ")}
                    </span>
                  ) : (
                    item.item_code && (
                      <span className="text-xs text-muted-foreground/70 font-mono">
                        {item.item_code}
                      </span>
                    )
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
